import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ClipboardList, ArrowRight, Users } from "lucide-react";
import VisitorSurveyDialog from "@/components/home/VisitorSurveyDialog";

export default function PublicSurveysSection() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/public/surveys-active")
      .then((r) => setSurveys((r.data?.items || []).slice(0, 6)))
      .catch(() => setSurveys([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="phenix-card p-10 animate-pulse bg-cream-100" />
      </section>
    );
  }

  if (!surveys.length) return null;

  return (
    <section id="sondages" className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-20" data-testid="public-surveys-section">
      <div className="text-center sm:text-left mb-7 sm:mb-9 max-w-2xl">
        <div className="phenix-eyebrow mb-3 inline-flex items-center gap-1.5">
          <ClipboardList size={11} strokeWidth={2.5} /> Donnez votre voix
        </div>
        <h2 className="heading-display text-[1.85rem] sm:text-[2.6rem] text-plum-900 leading-[1.05]">
          Sondages en cours
        </h2>
        <p className="mt-2 text-[15px] sm:text-[16px] text-plum-700/75">
          Vos avis dessinent la ville de demain. Répondez sans compte, validez en un clic.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="public-surveys-grid">
        {surveys.map((s) => (
          <button
            key={s.id}
            onClick={() => setActive(s)}
            data-testid={`public-survey-card-${s.id}`}
            className="phenix-card phenix-card-hover p-4 sm:p-5 text-left group flex flex-col gap-3 cursor-pointer"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-plum-600 to-pink-500 text-white grid place-items-center shadow-sm shrink-0">
                <ClipboardList size={17} strokeWidth={2.2} />
              </div>
              {s.vouchers_count > 0 ? (
                <span className="phenix-pill bg-sun-100 text-sun-700 text-[10px] uppercase inline-flex items-center gap-1">
                  🎁 {s.vouchers_count} bon{s.vouchers_count > 1 ? "s" : ""} à gagner
                </span>
              ) : (
                <span className="phenix-pill bg-cream-100 text-plum-700/60 text-[10px] uppercase">
                  {s.questions?.length || 0} question{(s.questions?.length || 0) > 1 ? "s" : ""}
                </span>
              )}
            </div>

            <div className="flex-1">
              <h3 className="font-display font-bold text-[16px] text-plum-900 leading-snug line-clamp-2 mb-1.5">
                {s.title}
              </h3>
              <p className="text-[12px] text-plum-700/65 line-clamp-2 leading-snug">{s.description}</p>
              {s.voucher_label && (
                <p className="text-[11px] text-pink-600 font-semibold mt-1.5 line-clamp-1">🎁 {s.voucher_label}</p>
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-cream-200">
              <div className="inline-flex items-center gap-1.5 text-[11px] text-plum-700/60">
                <Users size={11} strokeWidth={2.2} />
                {s.responses_count || 0} participant{(s.responses_count || 0) > 1 ? "s" : ""}
              </div>
              <span className="inline-flex items-center gap-1 text-[12px] font-bold text-pink-500 group-hover:gap-1.5 transition-all">
                Participer <ArrowRight size={12} />
              </span>
            </div>
          </button>
        ))}
      </div>

      <VisitorSurveyDialog
        survey={active}
        open={!!active}
        onClose={() => setActive(null)}
      />
    </section>
  );
}
